/**
 * 收费项目的支付页面
 */

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { service } from 'service';
import { useRequest, useBrowserTitle, useWxConfig, useLocationQuery, usePayNotice } from 'hook/index';
import { BlockLoading } from 'component/BlockLoading/BlockLoading';
import { bridge, isWechatMiniProgram, openMiniProgramPayLanding, JumpMethod, pageSchema } from 'util/index';
import { VipHero, PackageGrid, MemberBenefits } from 'component/pay/PayParts';
import styles from './Pay.module.scss';

interface AllProductMap {
    [k: string]: string;
}

interface BackendData {
    // 技能标志
    type: string;
    appName: string;
    // 技能中文名
    appNameCh: string | null;
    // 包几个月
    months: number;
    // 价格
    price: number;
    discount: number;
    originPrice: number;
}

const allProductMap: AllProductMap = {
    add10:"十以内加法",
    add20:"二十以内加法",
    add100:"一百以内加法",
    divide100:"一百以内除法",
    minus20:"二十以内减法",
    minus100:"一百以内减法",
    ts:"唐诗",
    szj:"三字经",
    by99:"九九乘法表",
    by99backstepping:"逆向九九乘法表",
    mental:"珠心算",
    dzg:"弟子规",
    mixoperator:"四则口算",
    myhomework:"我的作业",
    bigplus:"大数加法",
    myhardadd20:"二十以内加法",
};

// 可购买的产品
const onlineProduct = ['add100', 'mixoperator'];

const APP_PAY_CHANNEL = {
  wechat: 'wechat',
  alipay: 'alipay',
};

export default function Pay() {
    useBrowserTitle('VIP会员服务');
    useWxConfig();
    const history = useHistory();
    const query = useLocationQuery();
    const isApp = bridge.isInApp();
    const {
      checked: payNoticeChecked,
      toggle: togglePayNotice,
      viewVipNotice,
    } = usePayNotice();
    // 支付的来源技能名，有可能从设备扫码带过来
    const skillName = (query.appName || '') as string;
    // const [product, setProduct] = useState('');
    // 包几个月
    const [month, setMonth] = useState(() => {
        if (query) {
            return parseInt(query.months as string, 10) || 6;
        } else {
            return 6
        }
    });
    const [isSubmit, setIsSubmit] = useState(false);
    const [appPayChannel, setAppPayChannel] = useState(APP_PAY_CHANNEL.wechat);
    const appPayChannelList = [
      {
        value: APP_PAY_CHANNEL.wechat,
        text: '微信支付',
      },
      {
        value: APP_PAY_CHANNEL.alipay,
        text: '支付宝'
      },
    ];

    const {
        loading,
        data,
    } = useRequest<BackendData[]>({
        url: `/wechat/getproducts?payChannel=wechat&${window.location.search.substring(1)}`,
        method: 'GET',
        defaultData: [],
        defaultIsLoading: true,
    });
    const productList = data || [];
    // 首次请求到产品列表后，设置默认选项
    useEffect(() => {
        if (!productList || productList.length < 1) {
            return;
        }
        if (month) {
            return;
        }
        // setProduct(productList[0].appName);
        setMonth(productList[0].months);
    }, [productList, month]);
    if (loading) {
        return (
            <BlockLoading/>
        );
    }
    const currentProduct = productList.find((obj: any) => { return obj.months === month});
    let price = '--';
    if (currentProduct) {
        price = (currentProduct.price / 100).toFixed(2);
    }
    // 使用微信支付
    function onRequestWechatPay() {
        service.post('/wx/wxPaySkill', {
            serviceType: skillName || currentProduct && currentProduct.appName,
            months: month,
        })
            .then(function(out){
                setIsSubmit(false);
                if (out.status === 200 && out.data) {
                    if (out.data.status === 0) {
                        const data = out.data.data;
                        // alert(JSON.stringify(data));
                        WeixinJSBridge.invoke(
                            'getBrandWCPayRequest', data,
                            function(res: any) {
                                if (res.err_msg === "get_brand_wcpay_request:ok" ){
                                    // 微信提示 err_msg 不绝对可靠，故成功页以服务端开通状态为准展示。
                                    // 不再 alert+closeWindow（瞬时且无可回看确认），改为跳转持久的支付成功页 + 引导关注公众号。
                                    const orderNo = (data && data.out_trade_no) || '';
                                    history.replace(`/mp/pay/success?payType=${encodeURIComponent('微信支付')}&money=${encodeURIComponent(price)}&orderNo=${encodeURIComponent(orderNo)}`);
                                } else {
                                    console.log(JSON.stringify(res));
                                    alert('支付失败');
                                }
                            });
                    } else {
                        throw new Error(out.data.message);
                    }
                } else {
                    throw new Error(`网络异常，请稍后再试`);
                }
            })
            .catch(function(err: Error){
                setIsSubmit(false);
                alert(err.message);
            });
    }

    // 在语音口算APP里，使用支付宝SDK支付
    async function onRequestAlipay() {
      if (isSubmit) {
        return;
      }
      setIsSubmit(true);
      try {
        const res = await service.post('/alipay/createAppPayOrder', {
          serviceType: skillName || currentProduct && currentProduct.appName,
          months: month,
        });
        if (res.status === 200 && res.data && res.data.status === 0) {
          bridge.invokeAlipayForApp(res.data.data.res)
            .then((payResult: any) => {
              console.log('payResult=', payResult);
              // alert('支付结果正常');
              if (payResult.resultStatus === '9000') {
                // 支付成功 → 跳持久成功页（展示开通状态 + 引导关注公众号），不再用瞬时 alert
                let money = '';
                try {
                  const temp = JSON.parse(payResult.result);
                  money = temp.alipay_trade_app_pay_response.total_amount;
                } catch (err) {
                  console.error('alipay parse res money error ', err);
                }
                const orderNo = (res.data.data && res.data.data.out_trade_no) || '';
                history.replace(`/mp/pay/success?payType=${encodeURIComponent('支付宝')}&money=${encodeURIComponent(money)}&orderNo=${encodeURIComponent(orderNo)}`);
              }
            })
            .catch((err) => {
              console.error('onRequestAlipay result error', err);
              alert('支付结果异常：' + err.message);
            });
        } else {
          throw new Error('创建订单异常');
        }
        // alert('支付宝创建订单返回接口\n' + JSON.stringify(res));
      } catch (err) {
        alert(err.message);
      }
      setIsSubmit(false);
    }
  // 在语音口算APP里，使用微信SDK支付
  async function onRequestAppWechatPay() {
    if (isSubmit) {
      return;
    }
    setIsSubmit(true);
    try {
      const res = await service.post('/weixin/createAppPayOrder', {
        serviceType: skillName || currentProduct && currentProduct.appName,
        months: month,
      });
      if (res.status === 200 && res.data && res.data.status === 0) {
        bridge.invokeWechatForApp(JSON.stringify(res.data.data))
          .then((payResult: any) => {
            console.log('payResult=', payResult);
            // alert('支付结果正常');
            if (payResult.errCode === 0) {
              // 支付成功 → 跳持久成功页（展示开通状态 + 引导关注公众号），不再用瞬时 alert
              let money = '';
              try {
                const temp = JSON.parse(payResult.result);
                money = temp.alipay_trade_app_pay_response.total_amount;
              } catch (err) {
                console.error('wechatPay parse res money error ', err);
              }
              const orderNo = (res.data.data && res.data.data.out_trade_no) || '';
              history.replace(`/mp/pay/success?payType=${encodeURIComponent('微信')}&money=${encodeURIComponent(money)}&orderNo=${encodeURIComponent(orderNo)}`);
            }
          })
          .catch((err) => {
            console.error('invokeWechatForApp result error', err);
            alert('支付结果异常：' + err.message);
          });
      } else {
        throw new Error('创建订单异常');
      }
      // alert('支付宝创建订单返回接口\n' + JSON.stringify(res));
    } catch (err) {
      alert(err.message);
    }
    setIsSubmit(false);
  }
    // 支付按钮
    function onClickPay() {
      if (!payNoticeChecked) {
        alert('请先阅读并同意《小学僮会员服务协议》');
        return;
      }
      if (isApp) {
        if (appPayChannel === APP_PAY_CHANNEL.wechat) {
          onRequestAppWechatPay();
        } else {
          onRequestAlipay();
        }
      } else if (isWechatMiniProgram()) {
        // 微信小程序
        openMiniProgramPayLanding({
          serviceType: skillName || (currentProduct && currentProduct.appName) as string,
          months: month,
          jumpMethod: JumpMethod.navigateTo,
          targetUrl: `${pageSchema.miniappTab}/pages/company_my/company_my`,
        });
      } else {
        // 微信公众号
        onRequestWechatPay();
      }
    }
    const payBtnText = price !== '--' ? `支付 ¥${price}` : '支付';
    return (
        <div className={styles.page}>
            {/* VIP 权益头卡 */}
            <VipHero />

            {/* 选择套餐 */}
            <PackageGrid productList={productList} month={month} onSelect={setMonth} />

            {/* 支付方式（仅 App 内多渠道时显示） */}
            {
                isApp && (
                    <div className={styles.card}>
                        <div className={styles.secTitle}><span className={styles.secBar} /><span>支付方式</span></div>
                        <div className={styles.channelRow}>
                            {
                                appPayChannelList.map((obj: any) => {
                                    const on = appPayChannel === obj.value;
                                    return (
                                        <div
                                            key={obj.value}
                                            className={`${styles.channelOpt} ${on ? styles.channelOptOn : ''}`}
                                            onClick={() => setAppPayChannel(obj.value)}
                                        >
                                            {obj.text}
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                )
            }

            {/* 会员权益 */}
            <MemberBenefits />

            {/* 协议 + 支付 */}
            <div className={styles.payWrap}>
                <div className={styles.agree}>
                    <span
                        className={`${styles.agreeBox} ${payNoticeChecked ? styles.agreeBoxOn : ''}`}
                        onClick={togglePayNotice}
                    >✓</span>
                    <span onClick={togglePayNotice}>已阅读并同意</span>
                    <span className={styles.agreeLink} onClick={viewVipNotice}>《小学僮会员服务协议》</span>
                </div>
                <div
                    onClick={onClickPay}
                    className={`${styles.payBtn} ${isSubmit ? styles.payBtnDisabled : ''}`}
                >{payBtnText}</div>
                <div className={styles.note}>虚拟商品不支持退换货，请在购买前确认。</div>
            </div>
        </div>
    );
}
